import React, { useRef, useEffect, useCallback, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import {
  getNodeTypeConfig,
  getEdgeConfig,
  getCommunityColor,
  getPersonDetails,
  formatPersonName,
  getStatusConfig,
} from '../../../services/networkIntelligence.service';

/**
 * Force-directed network graph visualization component.
 * Displays the association network with the focal node highlighted.
 * Includes hover tooltips and click handling for investigation.
 */
const NetworkVisualization = ({
  data,
  width = 600,
  height = 400,
  onNodeClick,
  onNodeSelect,
}) => {
  const graphRef = useRef();
  const [hoveredNode, setHoveredNode] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [personCache, setPersonCache] = useState(new Map());

  // Process data for force-graph library
  const graphData = React.useMemo(() => {
    if (!data || !data.nodes || !data.links) {
      return { nodes: [], links: [] };
    }

    // Process nodes
    const nodes = data.nodes.map(node => ({
      id: node.id,
      personId: node.personId,
      isFocal: node.isFocal,
      depth: node.depth,
      communityId: node.communityId,
      connections: node.connections,
      ...getNodeTypeConfig(node.isFocal, node.depth),
    }));

    // Process links
    const links = data.links.map(link => ({
      source: link.source,
      target: link.target,
      weight: link.weight,
      interactionCount: link.interactionCount,
      ...getEdgeConfig(link.weight),
    }));

    return { nodes, links };
  }, [data]);

  // Center on focal node after mount
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0) {
      const focalNode = graphData.nodes.find(n => n.isFocal);
      if (focalNode) {
        setTimeout(() => {
          graphRef.current.centerAt(0, 0, 500);
          graphRef.current.zoom(1.5, 500);
        }, 500);
      }
    }
  }, [graphData]);

  // Load person details on hover
  const loadPersonDetails = async (personId) => {
    if (personCache.has(personId)) return;

    try {
      const details = await getPersonDetails(personId);
      if (details) {
        setPersonCache(prev => new Map(prev).set(personId, details));
      }
    } catch (error) {
      console.error('Failed to load person details:', error);
    }
  };

  // Node canvas rendering
  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const label = node.personId.toString();
    const fontSize = Math.min(12 / globalScale, node.size / 2);
    const nodeColor = node.communityId !== null && node.communityId !== undefined
      ? getCommunityColor(node.communityId)
      : node.color;

    // Draw node circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, node.size, 0, 2 * Math.PI);
    ctx.fillStyle = node.isFocal ? '#3B82F6' : nodeColor;
    ctx.fill();

    // Draw border for focal node
    if (node.isFocal) {
      ctx.strokeStyle = '#1E40AF';
      ctx.lineWidth = 3 / globalScale;
      ctx.stroke();
    }

    // Draw highlight ring for hovered node
    if (hoveredNode && hoveredNode.id === node.id) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.size + 4, 0, 2 * Math.PI);
      ctx.strokeStyle = '#FBBF24';
      ctx.lineWidth = 2 / globalScale;
      ctx.stroke();
    }

    // Draw label
    ctx.font = `${fontSize}px Sans-Serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(label, node.x, node.y);
  }, [hoveredNode]);

  // Handle node hover
  const handleNodeHover = useCallback((node, prevNode) => {
    setHoveredNode(node);
    if (node) {
      loadPersonDetails(node.personId);
    }
  }, []);

  // Handle mouse move for tooltip position
  const handleMouseMove = useCallback((event) => {
    if (hoveredNode) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPos({
        x: event.clientX - rect.left + 15,
        y: event.clientY - rect.top - 10,
      });
    }
  }, [hoveredNode]);

  // Handle node click
  const handleNodeClick = useCallback((node) => {
    if (onNodeSelect) {
      const person = personCache.get(node.personId);
      onNodeSelect(node.personId, {
        personId: node.personId,
        isFocal: node.isFocal,
        depth: node.depth,
        communityId: node.communityId,
        connections: node.connections,
        details: person,
      });
    }
    if (onNodeClick) {
      onNodeClick({
        personId: node.personId,
        isFocal: node.isFocal,
        depth: node.depth,
        communityId: node.communityId,
        connections: node.connections,
      });
    }
  }, [onNodeClick, onNodeSelect, personCache]);

  if (!data || graphData.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-center text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <p>No network data available</p>
          <p className="text-sm">Build the network graph first</p>
        </div>
      </div>
    );
  }

  const hoveredPerson = hoveredNode ? personCache.get(hoveredNode.personId) : null;
  const hoveredStatus = hoveredPerson ? getStatusConfig(hoveredPerson.status || hoveredPerson.Status) : null;

  return (
    <div
      className="relative bg-gray-900 rounded-lg overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <ForceGraph2D
        ref={graphRef}
        graphData={graphData}
        width={width}
        height={height}
        nodeCanvasObject={nodeCanvasObject}
        nodePointerAreaPaint={(node, color, ctx) => {
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size + 5, 0, 2 * Math.PI);
          ctx.fillStyle = color;
          ctx.fill();
        }}
        linkWidth={link => link.width}
        linkColor={link => link.color}
        linkDirectionalParticles={link => link.weight > 0.5 ? 2 : 0}
        linkDirectionalParticleWidth={2}
        onNodeClick={handleNodeClick}
        onNodeHover={handleNodeHover}
        cooldownTicks={100}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.3}
        backgroundColor="#111827"
      />

      {/* Hover Tooltip */}
      {hoveredNode && (
        <div
          className="absolute pointer-events-none bg-white rounded-lg shadow-xl p-3 z-10 max-w-xs"
          style={{
            left: Math.min(tooltipPos.x, width - 200),
            top: Math.min(tooltipPos.y, height - 150),
          }}
        >
          <div className="font-bold text-gray-900">
            {hoveredPerson ? formatPersonName(hoveredPerson) : `Person #${hoveredNode.personId}`}
          </div>
          <div className="text-xs text-gray-500 mb-2">ID: {hoveredNode.personId}</div>

          {hoveredPerson && hoveredStatus && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mb-2"
              style={{ backgroundColor: hoveredStatus.bgColor, color: hoveredStatus.color }}
            >
              {hoveredStatus.label}
            </span>
          )}

          <div className="text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-500">Depth:</span>
              <span className="font-medium">
                {hoveredNode.isFocal ? 'Subject' : hoveredNode.depth === 1 ? 'Direct' : `${hoveredNode.depth} hops`}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Connections:</span>
              <span className="font-medium">{hoveredNode.connections || 0}</span>
            </div>
            {hoveredNode.communityId !== null && hoveredNode.communityId !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-500">Community:</span>
                <span
                  className="font-medium px-1.5 rounded text-white"
                  style={{ backgroundColor: getCommunityColor(hoveredNode.communityId) }}
                >
                  #{hoveredNode.communityId}
                </span>
              </div>
            )}
          </div>

          <div className="mt-2 pt-2 border-t text-xs text-blue-600">
            Click to view details
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-2 left-2 bg-gray-800 bg-opacity-90 p-2 rounded text-xs text-white">
        <div className="font-semibold mb-1">Legend</div>
        <div className="flex items-center gap-1 mb-1">
          <span className="w-3 h-3 rounded-full bg-blue-500 inline-block"></span>
          <span>Subject (Focal)</span>
        </div>
        <div className="flex items-center gap-1 mb-1">
          <span className="w-2 h-2 rounded-full bg-purple-500 inline-block"></span>
          <span>Direct Connection</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-pink-500 inline-block"></span>
          <span>Indirect Connection</span>
        </div>
      </div>

      {/* Stats */}
      <div className="absolute top-2 right-2 bg-gray-800 bg-opacity-90 p-2 rounded text-xs text-white">
        <div><span className="text-gray-400">Nodes:</span> {data.totalNodes}</div>
        <div><span className="text-gray-400">Links:</span> {data.totalLinks}</div>
        <div><span className="text-gray-400">Communities:</span> {data.communitiesFound || 0}</div>
      </div>

      {/* Instructions */}
      <div className="absolute bottom-2 right-2 bg-gray-800 bg-opacity-90 p-2 rounded text-xs text-gray-300">
        <div>Hover: View info</div>
        <div>Click: Open details</div>
        <div>Drag: Move nodes</div>
        <div>Scroll: Zoom</div>
      </div>
    </div>
  );
};

export default NetworkVisualization;
